<template>
  <div id="meeting-meta">
      <k-form :columns="10" title="Meeting Details" icon="fas fa-cogs">
      <k-form-section :column-span="6">
        <k-form-field label="Title" :required="true" :field-id="'meeting-title-input'">
          <input v-model="meetingTitle"
            id="meeting-title-input"
            type="text"
            class="form-control"
            placeholder="Name title"/>
          <small>Please keep the title generic and not programme specific</small>
        </k-form-field>
        <k-form-field label="Description" :required="true" :field-id="'meeting-description-input'">
          <textarea v-model="meetingDescription"
            id="meeting-description-input"
            class="form-control"
            placeholder="Enter description here..."
            rows="3"/>
          <k-character-count :value-length="meetingDescription.length" :max-length="256" />
        </k-form-field>
        <k-form-field>
          <word-tags v-model="meetingTags" textInputPlaceholder="Add a tag"></word-tags>
        </k-form-field>
      </k-form-section>
      <k-form-section :column-span="4">
        <k-form-field label="Support Function" :required="true" tooltip="The support role of the intended meeting owner, e.g. coach, data mentor">
          <k-dropdown v-model="supportFunction" :options="learnerSupportOptions"></k-dropdown>
        </k-form-field>
        <k-form-field label="Is Ad-hoc meeting?">
          <k-toggle v-model="isAdhoc" :round="true" title="Check this box if the meeting is ad-hoc"></k-toggle>
          <small>Indicates that this meeting is meant to be scheduled on an ad-hoc basis only, i.e. <b>not</b> to be used for a programme's standard meetings.</small>
        </k-form-field>
        <k-form-field label="Share with Employer?">
          <k-toggle v-model="shareWithEmployerDefault" :round="true" title="Check this box to share meeting notes with employer by default"></k-toggle>
          <small>The default employer visibility for this instances of this meeting. A meeting owner can override this for individual instances of the meeting.</small>
        </k-form-field>
        <k-form-field label="Has Form?">
          <k-toggle v-model="hasForm" :round="true" title="Check this box to create form"></k-toggle>
          <small>Add a form to capture notes for this meeting, to be completed by the meeting owner.</small>
        </k-form-field>
        <k-form-field v-if="hasForm" label="Requires Learner Signature?">
          <k-toggle v-model="requiresLearnerSignature" :round="true" title="Check this box to require a learner signature for this meeting"></k-toggle>
          <small>Require the learner to sign the meeting form before the meeting can be completed.</small>
        </k-form-field>
      </k-form-section>
    </k-form>
    <hr class="divider-hr" v-if="hasForm">
  </div>
</template>

<style scoped>
#meeting-meta {
  margin-bottom: 15px;
}

.divider-hr {
  border: 1px solid var(--kate-panel-alt);
}

</style>

<script>
import {
  KForm, KFormField, KFormSection, KToggle, KCharacterCount,
} from '@base-components/forms';
import KDropdown from '@base-components/k-dropdown.vue';
import WordTags from '../../../components/word-tags.vue';

const LEARNER_SUPPORT_OPTIONS = [
  {
    id: 'coach',
    name: 'Coach',
  },
  {
    id: 'data_mentor',
    name: 'Data Mentor',
  },
];

export default {
  components: {
    KForm,
    KFormSection,
    KFormField,
    KDropdown,
    KToggle,
    WordTags,
    KCharacterCount,
  },

  watch: {
    meetingTitle(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, title: newValue });
      this.$emit('isValid', this.isValid);
    },
    meetingDescription(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, description: newValue });
      this.$emit('isValid', this.isValid);
    },
    meetingTags: {
      handler(newValue) {
        this.$emit('update:modelValue', { ...this.modelValue, meetingTags: newValue });
        this.$emit('isValid', this.isValid);
      },
      deep: true,
    },
    supportFunction(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, supportFunction: newValue });
      this.$emit('isValid', this.isValid);
    },
    hasForm(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, hasForm: newValue });
      this.$emit('isValid', this.isValid);
    },
    isAdhoc(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, isAdhoc: newValue });
      this.$emit('isValid', this.isValid);
    },
    requiresLearnerSignature(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, requiresLearnerSignature: newValue });
      this.$emit('isValid', this.isValid);
    },
    shareWithEmployerDefault(newValue) {
      this.$emit('update:modelValue', { ...this.modelValue, shareWithEmployerDefault: newValue });
      this.$emit('isValid', this.isValid);
    },
  },

  mounted() {
    this.$emit('isValid', this.isValid);
  },

  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      learnerSupportOptions: LEARNER_SUPPORT_OPTIONS,
      meetingTitle: this.modelValue.title || '',
      meetingDescription: this.modelValue.description || '',
      meetingTags: this.modelValue.meetingTags || [],
      hasForm: this.modelValue.hasForm || false,
      supportFunction: this.modelValue.supportFunction || 'coach',
      isAdhoc: this.modelValue.isAdhoc || false,
      requiresLearnerSignature: this.modelValue.requiresLearnerSignature ?? true,
      shareWithEmployerDefault: this.modelValue.shareWithEmployerDefault ?? true,
    };
  },

  computed: {
    isValid() {
      return this.meetingTitle.length > 0 && this.meetingDescription.length > 0
          && this.meetingTitle.length < 128 && this.meetingDescription.length < 256
          && ['coach', 'data_mentor'].indexOf(this.supportFunction) !== -1;
    },
  },
};
</script>
